/**
 * @author เต้ย
 * @description Class สำหรับจัดการ HTTP requests ผ่าน axios
 * รวมการจัดการ loading, error handling, และ CSRF token
 */
class AxiosClient {
    constructor(template) {
        this.template = template;
        this.baseUrl = template?.baseUrl || $('meta[name="baseUrl"]').attr('content');
        this.csrfTokenName =
            template?.csrfTokenName || $('meta[name="csrf-token"]').attr('data-name') || 'csrf_hash_name';
        this.isLoading = false;

        // Setup default axios configuration
        this.setupAxiosDefaults();
    }

    /**
     * @author เต้ย
     * @description ตั้งค่า axios defaults
     */
    setupAxiosDefaults() {
        axios.defaults.baseURL = this.baseUrl;
        axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
        // ต้องเป็น common ไม่ใช่ post — ฝั่ง PHP ใช้ is_ajax_request() ตัดสินใจว่าจะ
        // session_write_close() ปลด lock หรือไม่ ถ้า GET ไม่มี header นี้ทุก request
        // ของ user คนเดียวกันจะถูก flock() ต่อคิวกันทีละอัน
        axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    }

    /**
     * @author เต้ย
     * @description ดึง CSRF token ปัจจุบัน
     */
    getCsrfToken() {
        return $('meta[name="csrf-token"]').attr('content');
    }

    /**
     * @author เต้ย
     * @description อัปเดต CSRF token
     * @param {string} token - CSRF token ใหม่
     */
    updateCsrfToken(token) {
        $('meta[name="csrf-token"]').attr('content', token);
        if (this.template) {
            this.template.setCsrfToken(token);
        }
    }

    /**
     * @author เต้ย
     * @description แสดง loading state
     * @param {string} message - ข้อความที่จะแสดง
     */
    showLoading(message = 'กำลังโหลด...') {
        if (this.isLoading) return;

        this.isLoading = true;
        $('.is-invalid').removeClass('is-invalid');
        $('.invalid-feedback').remove();

        Swal.fire({
            title: message,
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });
    }

    /**
     * @author เต้ย
     * @description ซ่อน loading state
     */
    hideLoading() {
        this.isLoading = false;
        Swal.close();
    }

    /**
     * @author เต้ย
     * @description จัดการ error response
     * @param {object} error - Error object จาก axios
     */
    // ห้ามแสดง toast หรือแปะ validation error ที่นี่ — axios_interceptor.js ถูกโหลดทุกหน้าผ่าน
    // default_js.php และทำสองอย่างนั้นให้ครบทุก status code อยู่แล้ว ถ้าทำซ้ำที่ชั้นนี้ ผู้ใช้จะเห็น
    // toast ซ้อนกันสองอันต่อ error หนึ่งครั้ง และข้อความใต้ช่องกรอกซ้ำสองบรรทัด
    handleError(error) {
        this.hideLoading();

        if (error.response?.data?.csrf_token) {
            this.updateCsrfToken(error.response.data.csrf_token);
        }

        return Promise.reject(error);
    }

    /**
     * @author เต้ย
     * @description แสดง validation errors
     * @param {object} errors - Object ของ validation errors
     */
    showValidationErrors(errors) {
        for (let key in errors) {
            const input =
                $(`.validate-input[name="${key}"]`).length > 0 ? $(`.validate-input[name="${key}"]`) : $(`#${key}`);

            if (input.length > 0) {
                // กรณีเป็น input-group
                if (input.parent().hasClass('input-group')) {
                    $(`<span class="invalid-feedback d-block">${errors[key]}</span>`).insertAfter(input.parent());
                } else {
                    $(`<span class="invalid-feedback">${errors[key]}</span>`).insertAfter(input);
                }
                input.addClass('is-invalid');
            }
        }
    }

    /**
     * @author เต้ย
     * @description จัดการ response ที่สำเร็จ
     * @param {object} response - Response object จาก axios
     */
    handleResponse(response) {
        this.hideLoading();

        // อัพเดท CSRF token ถ้ามี
        if (response.data.csrf_token) {
            this.updateCsrfToken(response.data.csrf_token);
        }

        return response;
    }

    /**
     * @author เต้ย
     * @description เตรียม data สำหรับ request
     * @param {object|FormData} data - ข้อมูลที่จะส่ง
     * @param {string} method - HTTP method
     */
    prepareData(data, method) {
        if (method.toLowerCase() === 'get') {
            return {
                ...data,
                [this.csrfTokenName]: this.getCsrfToken(),
            };
        }

        if (data instanceof FormData) {
            data.append(this.csrfTokenName, this.getCsrfToken());
            return data;
        }

        return {
            ...data,
            [this.csrfTokenName]: this.getCsrfToken(),
        };
    }

    /**
     * @author เต้ย
     * @description GET request
     * @param {string} url - URL endpoint
     * @param {object} params - Query parameters
     * @param {object} options - Request options
     */
    async get(url, params = {}, options = {}) {
        try {
            if (!options.skipLoading) {
                this.showLoading(options.loadingMessage);
            }

            const paramsData = this.prepareData(params, 'get');

            const response = await axios.get(url, {
                params: paramsData,
                ...options,
            });

            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * @author เต้ย
     * @description POST request
     * @param {string} url - URL endpoint
     * @param {object|FormData} data - ข้อมูลที่จะส่ง
     * @param {object} options - Request options
     */
    async post(url, data = {}, options = {}) {
        try {
            if (!options.skipLoading) {
                this.showLoading(options.loadingMessage);
            }

            const response = await axios.post(url, this.prepareData(data, 'post'), options);
            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * @author เต้ย
     * @description POST request แบบ JSON body — รองรับ nested array/object
     * ใช้กับ PHP proxy ที่อ่าน json_decode(file_get_contents('php://input'))
     * CSRF ส่งผ่าน header X-CSRF-Token (body เป็น JSON ล้วน)
     * @param {string} url - URL endpoint
     * @param {object} data - payload (nested array/object ได้ตรง ๆ)
     * @param {object} options - Request options
     */
    async postJson(url, data = {}, options = {}) {
        try {
            if (!options.skipLoading) {
                this.showLoading(options.loadingMessage);
            }

            const headers = {
                ...(options.headers || {}),
                'Content-Type': 'application/json',
                'X-CSRF-Token': this.getCsrfToken(),
            };

            const response = await axios.post(url, data, { ...options, headers });
            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * @author เต้ย
     * @description PUT request
     * @param {string} url - URL endpoint
     * @param {object|FormData} data - ข้อมูลที่จะส่ง
     * @param {object} options - Request options
     */
    async put(url, data = {}, options = {}) {
        try {
            if (!options.skipLoading) {
                this.showLoading(options.loadingMessage);
            }

            const response = await axios.put(url, this.prepareData(data, 'put'), options);
            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * @author เต้ย
     * @description DELETE request
     * @param {string} url - URL endpoint
     * @param {object} data - ข้อมูลที่จะส่ง
     * @param {object} options - Request options
     */
    async delete(url, data = {}, options = {}) {
        try {
            if (!options.skipLoading) {
                this.showLoading(options.loadingMessage);
            }

            const response = await axios.delete(url, {
                data: this.prepareData(data, 'delete'),
                ...options,
            });

            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * @author เต้ย
     * @description PATCH request
     * @param {string} url - URL endpoint
     * @param {object|FormData} data - ข้อมูลที่จะส่ง
     * @param {object} options - Request options
     */
    async patch(url, data = {}, options = {}) {
        try {
            if (!options.skipLoading) {
                this.showLoading(options.loadingMessage);
            }

            const response = await axios.patch(url, this.prepareData(data, 'patch'), options);
            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * @author เต้ย
     * @description สร้าง request แบบกำหนดเอง
     * @param {object} config - axios config object
     */
    async request(config) {
        try {
            if (!config.skipLoading) {
                this.showLoading(config.loadingMessage);
            }

            // เตรียม data/params ตาม method
            if (config.method?.toLowerCase() === 'get' && config.params) {
                config.params = this.prepareData(config.params, 'get');
            } else if (config.data) {
                config.data = this.prepareData(config.data, config.method || 'post');
            }

            const response = await axios.request(config);
            return this.handleResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }
}
